import React, {useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';

import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import {
  Box, Flex, Heading, Image, Text, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Swiper} from '@amblique/base/app/components/shared/swiper';
import Hotspot from '@amblique/king-living/app/components/hotspot';
import ShopButton from '@amblique/king-living/app/components/shop-button';

const sampleImages = [
  {
    src: getAssetUrl('static/img/shop-look-1.png'),
    alt: 'Modern living room with green sofa',
    hotspots: [
      {top: {base: '60%', md: '59.6%'}, left: {base: '35%', md: '34.1%'}}, // Main sofa
      {top: {base: '80%', md: '82.8%'}, left: {base: '65%', md: '62.6%'}}, // Coffee table
      {top: {base: '50%', md: '51.5%'}, left: {base: '90%', md: '89%'}}, // Side items
      {top: {base: '50%', md: '51.5%'}, left: {base: '80%', md: '77.8%'}}, // Additional items
      {top: {base: '70%', md: '74%'}, left: {base: '5%', md: '2.5%'}} // Side table
    ],
  },
  {
    src: getAssetUrl('static/img/shop-look-2.png'),
    alt: 'Curved ceiling living space',
    hotspots: [
      {top: {base: '15%', md: '11.9%'}, left: {base: '40%', md: '40%'}}, // Ceiling light
      {top: {base: '65%', md: '62.2%'}, left: {base: '55%', md: '55.25%'}} // Seating area
    ],
  },
  {
    src: getAssetUrl('static/img/shop-look-3.png'),
    alt: 'Bedroom with modern design',
    hotspots: [],
  },
  {
    src: getAssetUrl('static/img/shop-look-1.png'),
    alt: 'Modern living room with green sofa',
    hotspots: [],
  },
  {
    src: getAssetUrl('static/img/shop-look-3.png'),
    alt: 'Bedroom with modern sofa',
    hotspots: [],
  }
];

/**
 * Gallery Carousel
 * @param {Object} props
 * @param {string} props.title
 * @param {string} props.subtitle
 * @param {Array} props.images
 * @param {Function} props.onHotspotClick
 * @param {Function} props.onShopClick
 * @param {Function} props.onSeeAllClick
 */
const GalleryCarousel = ({
  title = 'Shop the look and transform your space with timeless elegance',
  subtitle = 'Elevate your living space with accessories designed to enhance style and functionality, transforming your sofa into the ultimate centrepiece of comfort and convenience.',
  images = sampleImages,
  onHotspotClick,
  onShopClick,
  onSeeAllClick,
  ...props
}) => {
  const styles = useMultiStyleConfig('GalleryCarousel', {});

  const handleHotspotClick = useCallback((imageIndex, hotspotIndex) => {
    if (onHotspotClick) {
      onHotspotClick(imageIndex, hotspotIndex);
    }
  }, [onHotspotClick]);

  const handleShopClick = useCallback((imageIndex) => {
    if (onShopClick) {
      onShopClick(imageIndex);
    }
  }, [onShopClick]);

  const handleSeeAllClick = useCallback(() => {
    if (onSeeAllClick) {
      onSeeAllClick();
    }
  }, [onSeeAllClick]);

  const swiperOptions = useMemo(() => ({
    navigation: false,
    pagination: false,
    scrollbar: false,
    autoHeight: false,
    spaceBetween: 4,
    slidesPerView: 'auto',
    slidesPerGroup: 1,
    freeMode: true,
    breakpoints: {
      // Mobile
      320: {
        spaceBetween: 4,
        slidesPerView: 'auto',
      },
      // Desktop
      768: {
        spaceBetween: 4,
        slidesPerView: 'auto',
      },
    },
  }), []);

  return (
    <Box {...styles.container} {...props}>
      <Box {...styles.containerInner}>
        {/* Header Section */}
        <Flex {...styles.headerSection}>
          <Heading as="h2" {...styles.mainHeading}>
            {title}
          </Heading>
          <Box {...styles.rightColumn}>
            <Text {...styles.subheading}>
              {subtitle}
            </Text>
          </Box>
        </Flex>

        {/* Image Container */}
        <Flex {...styles.imageContainer}>
          <Swiper
            options={swiperOptions}
            presets=""
          >
            {images.map((img, index) => {
              const wrapProps = index === 1 ? styles.smallImageWrap : styles.largeImageWrap;

              return (
                <Box key={index} {...wrapProps}>
                  <Image alt={img?.alt} src={img?.src} {...styles.image} />

                  {(img?.hotspots || []).map((hotspot, idx) => (
                    <Hotspot
                      key={idx}
                      left={hotspot.left}
                      top={hotspot.top}
                      onClick={() => handleHotspotClick(index, idx)}
                    />
                  ))}

                  <Box {...styles.imageOverlay}>
                    <ShopButton onClick={() => handleShopClick(index)} />
                  </Box>
                </Box>
              );
            })}
          </Swiper>
        </Flex>

        {/* CTA Button */}
        <Box
          as="button"
          {...styles.ctaButton}
          onClick={handleSeeAllClick}
        >
          See all shop the look
        </Box>
      </Box>
    </Box>
  );
};

GalleryCarousel.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  images: PropTypes.array,
  onHotspotClick: PropTypes.func,
  onShopClick: PropTypes.func,
  onSeeAllClick: PropTypes.func,
};

export default GalleryCarousel;
